import axios from 'axios';

const BASE_URL = 'https://loyalty.bom.asia';
const API_BASE_URL = `${BASE_URL}/api/miniApp/checkin/events`;

// Get all events
export const getAllEvents = async (token) => {
  try {
    const response = await axios.get(API_BASE_URL, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    
    // Ensure we return an array based on actual API response format
    if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data.events)) {
      return response.data.data.events;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (response.data && Array.isArray(response.data.events)) {
      return response.data.events;
    } else {
      console.warn('⚠️ Unexpected API response format:', response.data);
      return [];
    }
  } catch (error) {
    console.error('❌ Get all events error:', error);
    throw error;
  }
};

// Get online events
export const getOnlineEvents = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/online`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Get online events error:', error);
    throw error;
  }
};

// Get offline events
export const getOfflineEvents = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/offline`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Get offline events error:', error);
    throw error;
  }
};

// Get event by ID
export const getEventById = async (eventId, token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/${eventId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Get event by ID error:', error);
    throw error;
  }
};

// Delete event
export const deleteEvent = async (eventId, token) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/${eventId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Delete event error:', error);
    throw error;
  }
};

// Update event status
export const updateEventStatus = async (eventId, status, token) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/${eventId}/status`, 
      { status }, 
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ Update event status error:', error);
    throw error;
  }
};

// Get upload info
export const getUploadInfo = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/upload-info`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Get upload info error:', error);
    throw error;
  }
};

// Create event
export const createEvent = async (eventData, token) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    Object.keys(eventData).forEach(key => {
      if (key !== 'eventImages' && key !== 'eventVideos' && key !== 'descriptionImages' && key !== 'descriptionVideos') {
        formData.append(key, eventData[key]);
      }
    });

    // Add files
    if (eventData.eventImages && Array.isArray(eventData.eventImages)) {
      eventData.eventImages.forEach(image => {
        formData.append('eventImages', image);
      });
    }
    
    if (eventData.eventVideos && Array.isArray(eventData.eventVideos)) {
      eventData.eventVideos.forEach(video => {
        formData.append('eventVideos', video);
      });
    }
    
    if (eventData.descriptionImages && Array.isArray(eventData.descriptionImages)) {
      eventData.descriptionImages.forEach(image => {
        formData.append('descriptionImages', image);
      });
    }
    
    if (eventData.descriptionVideos && Array.isArray(eventData.descriptionVideos)) {
      eventData.descriptionVideos.forEach(video => {
        formData.append('descriptionVideos', video);
      });
    }

    const response = await axios.post(API_BASE_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Create event error:', error);
    throw error;
  }
};

// Update event
export const updateEvent = async (eventId, eventData, token) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    Object.keys(eventData).forEach(key => {
      if (key !== 'eventImages' && key !== 'eventVideos' && key !== 'descriptionImages' && key !== 'descriptionVideos') {
        formData.append(key, eventData[key]);
      }
    });

    // Add files
    if (eventData.eventImages && Array.isArray(eventData.eventImages)) {
      eventData.eventImages.forEach(image => {
        formData.append('eventImages', image);
      });
    }
    
    if (eventData.eventVideos && Array.isArray(eventData.eventVideos)) {
      eventData.eventVideos.forEach(video => {
        formData.append('eventVideos', video);
      });
    }
    
    if (eventData.descriptionImages && Array.isArray(eventData.descriptionImages)) {
      eventData.descriptionImages.forEach(image => {
        formData.append('descriptionImages', image);
      });
    }
    
    if (eventData.descriptionVideos && Array.isArray(eventData.descriptionVideos)) {
      eventData.descriptionVideos.forEach(video => {
        formData.append('descriptionVideos', video);
      });
    }

    const response = await axios.put(`${API_BASE_URL}/${eventId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('❌ Update event error:', error);
    throw error;
  }
};

// Helper function to get token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
};

// Wrapper functions with automatic token injection
export const eventApi = {
  // Get all events with automatic token
  getAll: async () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    try {
      const result = await getAllEvents(token);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('❌ Event API getAll error:', error);
      return [];
    }
  },

  // Get online events with automatic token
  getOnline: async () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return getOnlineEvents(token);
  },

  // Get offline events with automatic token
  getOffline: async () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return getOfflineEvents(token);
  },

  // Get event by ID with automatic token
  getById: async (eventId) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return getEventById(eventId, token);
  },

  // Delete event with automatic token
  delete: async (eventId) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return deleteEvent(eventId, token);
  },

  // Update event status with automatic token
  updateStatus: async (eventId, status) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return updateEventStatus(eventId, status, token);
  },

  // Get upload info with automatic token
  getUploadInfo: async () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return getUploadInfo(token);
  },

  // Create event with automatic token
  create: async (eventData) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return createEvent(eventData, token);
  },

  // Update event with automatic token
  update: async (eventId, eventData) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    return updateEvent(eventId, eventData, token);
  }
};

export default eventApi;
